/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 */

/**
 * @module bulb-caption/utils
 */

import { enableViewPlaceholder, toWidgetEditable } from 'ckeditor5';
// eslint-disable-next-line import/no-cycle
import { blocksWithCaptions } from '../../editorconfig.constant';
// TODO CG: Add missing types
/**
 * Returns a function that creates a caption editable element for the given
 * {@link module:engine/view/document~Document}.
 *
 * @param {module:engine/view/view~View} view
 * @param {String} t localization translation function.
 * @returns {Function}
 */
export function captionElementCreator(view, t) {
  return (writer) => {
    const editable = writer.createEditableElement('figcaption');
    writer.setCustomProperty('mediaCaption', true, editable);
    editable.placeholder = t('editor_image_caption_placeholder');

    enableViewPlaceholder({
      view,
      element: editable,
    });

    return toWidgetEditable(editable, writer);
  };
}

/**
 * Returns the caption model element from a given embed element. Returns `null` if no caption is found.
 *
 * @param {module:engine/model/element~Element} element
 * @returns {module:engine/model/element~Element|null}
 */
export function getCaptionFromMedia(element) {
  for (const node of element.getChildren()) {
    if (!!node && node.is('element', 'caption')) {
      return node;
    }
  }
  return null;
}

/**
 * Helper function to determine if the current block is an acceptable block.
 * @param parent
 * @param element
 * @returns {Boolean}
 */
export function isAllowedBlock(parent, element): boolean {
  if (parent) {
    return blocksWithCaptions.some((blockType) => parent.hasClass(blockType));
  }
  return blocksWithCaptions.some((blockType) => element.is('element', blockType));
}

/**
 * {@link module:engine/view/matcher~Matcher} pattern.
 * Checks if a given element is a `<figcaption>` element that is placed
 * inside the element's `<figure>` parent.
 *
 * @param {module:engine/view/element~Element} element
 * @returns {Object|null} Returns the object accepted by {@link module:engine/view/matcher~Matcher} or
 * `null` if the element
 * cannot be matched.
 */
export function matchMediaCaption(element) {
  const { parent } = element;

  // Convert captions.
  if (element.name === 'figcaption' && parent && parent.name === 'figure' && isAllowedBlock(parent, element)) {
    return { name: true };
  }

  return null;
}

/**
 * Checks if the provided model element is one of the acceptable elements.
 *
 * @param {module:engine/model/element~Element} modelElement
 * @returns {Boolean}
 */
export function isAllowedElement(modelElement: Element): boolean {
  return !!modelElement && isAllowedBlock(null, modelElement);
}
